import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from 'redux/hooks'
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {Dialog} from "primereact/dialog";
import {checkPermissions} from "../../../../redux/permissions/permissionsUtils";
import {setIVRConfigurationDialogVisible, setIVRConfigurationErrorFields} from "../../../../redux/actions/actionsIVRConfiguration";
import {createIVRConfiguration, updateIVRConfiguration} from "../../../../redux/api/apiIVRConfiguration";
import {validateIVRConfigurationData} from "../../../../redux/validator/validator";
import IVRConfigurationDialogInfo from "../IVRConfigurationDialogInfo";
import IVRConfigurationDialogGeneralSettings from "../IVRConfigurationDialogGeneralSettings";

const IVRConfigurationDialog = (props: any) => {
    const dispatch = useAppDispatch()
    const ivrFull = useAppSelector(
        (state) => state.IVRConfigurationReducer.ivrConfigurationData
    );
    const ivrConfigurationData = ivrFull;
    const ivrConfiguration = (ivrFull?.configuration ?? null) as Record<
        string,
        unknown
    > | null;
    const ivrFlowId = ivrConfiguration?.flowId as number | string | null | undefined;
    const dialogVisible = useAppSelector(state => state.IVRConfigurationReducer.ivrConfigurationDialogVisible);
    const [activeIndex, setActiveIndex] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const errorFields = useAppSelector(state => state.IVRConfigurationReducer.errorFields);
    const ivrConfigurationPermissions = checkPermissions(rolesVision, 'IVRConfig');
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);

    const {
        visible: hasIVRConfigurationVisiblePermission,
        create: hasIVRConfigurationCreatePermission,
        update: hasIVRConfigurationUpdatePermission,
    } = ivrConfigurationPermissions;

    const showIvrSchemeTab =
        hasIVRConfigurationVisiblePermission &&
        ivrFlowId != null &&
        ivrFlowId !== "";

    const ivrSchemeTabIndex = showIvrSchemeTab ? 1 : -1;

    useEffect(() => {
        setActiveIndex(0);
    }, [dialogVisible]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (errorFields && errorFields?.name) {
            setActiveIndex(0);
        }
    }, [errorFields])

    const closeDialogVisible = () => {
        dispatch(setIVRConfigurationDialogVisible(false));
        dispatch(setIVRConfigurationErrorFields(null));
    }

    const payloadWithoutInternalKeys = () => {
        const raw = { ...((ivrFull ?? {}) as Record<string, unknown>) };
        delete raw._schemeReloadToken;
        return raw;
    };

    const handleUpdateIVRConfiguration = () => {
        const ivrConfigurationCopy = payloadWithoutInternalKeys();
        const validationErrors = validateIVRConfigurationData(
            ivrConfigurationCopy.configuration ?? ivrConfigurationCopy
        );
        if (!validationErrors) {
            dispatch(updateIVRConfiguration(ivrConfigurationCopy, jwtToken?.jwtToken));
            dispatch(setIVRConfigurationErrorFields(null));
        } else {
            dispatch(setIVRConfigurationErrorFields(validationErrors));
            if (validationErrors.name) {
                setActiveIndex(0);
            }
        }
    };

    const handleCreateIVRConfiguration = () => {
        const ivrConfigurationCopy = payloadWithoutInternalKeys();
        const validationErrors = validateIVRConfigurationData(
            ivrConfigurationCopy.configuration ?? ivrConfigurationCopy
        );
        if (!validationErrors) {
            dispatch(createIVRConfiguration(ivrConfigurationCopy, jwtToken?.jwtToken));
            dispatch(setIVRConfigurationErrorFields(null));
        } else {
            dispatch(setIVRConfigurationErrorFields(validationErrors));
            if (validationErrors.name) {
                setActiveIndex(0);
            }
        }
    };

    const showSaveChangesNotification = () => {
        dispatch(setIVRConfigurationDialogVisible(false));
    }

    const ivrCfgAudit = (() => {
        const root = (ivrFull ?? {}) as Record<string, unknown>;
        const conf = (ivrConfiguration ?? {}) as Record<string, unknown>;
        const nested = (conf.ivrConfigurationData as Record<string, unknown>) ?? {};
        const pick = (k: string) => nested[k] ?? conf[k] ?? root[k];
        return {
            createdByUserId: pick("createdByUserId") as string | null | undefined,
            createdDateTime: pick("createdDateTime") as string | null | undefined,
            updatedByUserId: pick("updatedByUserId") as string | null | undefined,
            updatedDateTime: pick("updatedDateTime") as string | null | undefined,
            userCreate: pick("userCreate") as string | null | undefined,
            userUpdate: pick("userUpdate") as string | null | undefined,
        };
    })();

    const createdUserTime = () => {
        const t = new Date(ivrCfgAudit?.createdDateTime ?? "").toLocaleString(
            "uk-UA"
        );
        return ivrCfgAudit?.createdDateTime ? t : "-";
    };

    const updatedUserTime = () => {
        const t = new Date(ivrCfgAudit?.updatedDateTime ?? "").toLocaleString(
            "uk-UA"
        );
        return ivrCfgAudit?.updatedDateTime ? t : "-";
    };

    const footer = (
        <div className="dialog-footer">
            <Divider/>
            <div className="buttons">
                <Button icon={windowWidth > 1248 ? "pi pi-ban" : ''}
                        className="p-button-outlined"
                        label="Відміна"
                        onClick={() => {
                            showSaveChangesNotification()
                        }}
                />
                {hasIVRConfigurationCreatePermission && !ivrFlowId && <Button icon={windowWidth > 1248 ? "pi pi-save" : ''}
                                                                                                  className=""
                                                                                                  label="Створити та зберегти"
                                                                                                  onClick={() => {
                                                                                                      handleCreateIVRConfiguration()
                                                                                                  }}
                />}
                {hasIVRConfigurationUpdatePermission && ivrFlowId && <Button icon={windowWidth > 1248 ? "pi pi-save" : ''}
                                                                                            label="Зберегти"
                                                                                            onClick={() => {
                                                                                                handleUpdateIVRConfiguration()
                                                                                            }}

                />}
            </div>
        </div>
    );

    return (
        <Dialog
            header="Налаштування IVR Конфігурації"
            footer={footer}
            visible={dialogVisible}
            dismissableMask={false}
            closeOnEscape={false}
            draggable={true}
            blockScroll={true}
            position="top"
            className='dialogInfo'
            style={{width: '95vw', maxWidth: "1200px", minHeight: "80vh", zIndex: '1101px !important'}}
            onHide={() => showSaveChangesNotification()}
        >
            <IVRConfigurationDialogInfo/>
            <Divider style={{margin: '0 0 20px 0'}}/>
            {ivrConfigurationData && <div className="company-info-popup">
                <div className="grid align-items-center">
                    <div className="field col-12 md:col-6">
                        {!!ivrFlowId && (
                            <div className="item pl-2">
                                ID: {ivrFlowId}
                            </div>
                        )}
                    </div>
                    <div className="field col-12 md:col-6 flex justify-content-end mb-0">
                        {!!ivrFlowId && (
                            <div className="field col-12 md:col-12 flex flex-wrap p-0 m-0">
                                {(ivrCfgAudit?.createdByUserId ||
                                    ivrCfgAudit?.createdDateTime) && (
                                    <div className="field col-12 md:col-12 flex flex-wrap m-0">
                                        <div className="field col-12 md:col-2 flex p-0 m-0">
                                            Створено:
                                        </div>
                                        <div className="field col-12 md:col-6 flex p-0 m-0">
                                            {ivrCfgAudit?.userCreate
                                                ? ivrCfgAudit.userCreate
                                                : "-"}
                                        </div>
                                        <div className="field col-12 md:col-4 flex p-0 m-0">
                                            {createdUserTime()}
                                        </div>
                                    </div>
                                )}
                                {(ivrCfgAudit?.updatedByUserId ||
                                    ivrCfgAudit?.updatedDateTime) && (
                                    <div className="field col-12 md:col-12 flex flex-wrap m-0">
                                        <div className="field col-12 md:col-2 flex p-0 m-0">
                                            Оновлено:
                                        </div>
                                        <div className="field col-12 md:col-6 flex p-0 m-0">
                                            {ivrCfgAudit?.userUpdate
                                                ? ivrCfgAudit.userUpdate
                                                : "-"}
                                        </div>
                                        <div className="field col-12 md:col-4 flex p-0 m-0">
                                            {updatedUserTime()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <TabView
                    className="user-dialog-info"
                    activeIndex={activeIndex}
                    onTabChange={(e) => {
                        if (ivrSchemeTabIndex >= 0 && e.index === ivrSchemeTabIndex) {
                            const base = `${window.location.origin}${window.location.pathname || ""}`;
                            const href = `${base}#/ivr_scheme/${ivrFlowId}?childWindow=1`;
                            window.open(href, "_blank");
                            return;
                        }
                        setActiveIndex(e.index);
                    }}
                >
                    <TabPanel header="Загальні налаштування">
                        {ivrConfigurationData && <IVRConfigurationDialogGeneralSettings/>}
                    </TabPanel>
                    {showIvrSchemeTab && (
                        <TabPanel header="IVR схема">{null}</TabPanel>
                    )}
                </TabView>
            </div>}
        </Dialog>
    )
}

export default IVRConfigurationDialog;

